import React from 'react';
import HoloCard from '../components/HoloCard';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';
import { kpiData, timelineEvents } from '../utils/mockData';

const Overview = () => {
  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', label: { backgroundColor: '#020617' } }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
    xAxis: [{
      type: 'category',
      boundaryGap: false,
      data: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
      axisLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#64748b', fontSize: 10 }
    }],
    yAxis: [{
      type: 'value',
      splitLine: { lineStyle: { color: '#0f172a', type: 'dashed' } },
      axisLabel: { color: '#64748b', fontSize: 10 }
    }],
    series: [
      {
        name: 'Throughput',
        type: 'line',
        smooth: true,
        lineStyle: { width: 3, color: '#06b6d4', shadowColor: 'rgba(6,182,212,0.5)', shadowBlur: 10 },
        showSymbol: false,
        areaStyle: {
          opacity: 0.15,
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(6,182,212,0.5)' }, { offset: 1, color: 'rgba(6,182,212,0)' }]
          }
        },
        data: [120, 132, 101, 134, 90, 230, 210]
      },
      {
        name: 'System Stress',
        type: 'line',
        smooth: true,
        lineStyle: { width: 2, color: '#f59e0b', type: 'dashed' },
        showSymbol: false,
        data: [20, 12, 11, 24, 60, 30, 20]
      }
    ]
  };

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2 text-cyan-500 font-mono text-[10px] uppercase tracking-[0.2em] mb-1">
            <SafeIcon name="Cpu" className="animate-pulse" />
            <span>Core Telemetry Uplink</span>
          </div>
          <h1 className="text-3xl font-light text-white tracking-tight">Command <span className="font-bold text-cyan-400 uppercase italic">Overview</span></h1>
        </div>
        <div className="flex items-center space-x-4">
          <div className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-bold text-emerald-400 flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-ping" />
            LIVE_SYNC
          </div>
          <div className="text-xs font-mono text-slate-500 uppercase">
            ID: CX-0042 // {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, i) => (
          <HoloCard key={kpi.id} delay={i * 0.05} className="!p-4 border-l-4 border-l-cyan-500/40">
            <div className="flex justify-between items-start mb-3">
              <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">{kpi.label}</span>
              <div className={`p-1.5 rounded bg-slate-900 border ${kpi.status === 'warning' ? 'border-amber-500/30 text-amber-500' : 'border-cyan-500/30 text-cyan-500'}`}>
                <SafeIcon name={kpi.icon} className="text-sm" />
              </div>
            </div>
            <div className="flex items-end justify-between">
              <span className={`text-3xl font-mono tracking-tighter ${kpi.status === 'warning' ? 'text-amber-400 text-glow-amber' : 'text-white text-glow'}`}>
                {kpi.value}
              </span>
              <div className={`flex items-center text-[10px] font-bold ${kpi.trend.startsWith('+') ? 'text-emerald-400' : 'text-amber-400'}`}>
                <SafeIcon name={kpi.trend.startsWith('+') ? 'TrendingUp' : 'TrendingDown'} className="mr-1" />
                {kpi.trend}
              </div>
            </div>
          </HoloCard>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <HoloCard title="Cluster Performance Matrix" delay={0.2} className="lg:col-span-2 min-h-[400px]">
          <div className="w-full h-full pt-4">
            <ReactECharts option={chartOptions} style={{ height: '320px', width: '100%' }} />
          </div>
        </HoloCard>

        <HoloCard title="Incident Vector Stream" delay={0.3} className="flex flex-col max-h-[400px]">
          <div className="flex-1 overflow-y-auto space-y-6 pr-2 custom-scrollbar">
            {timelineEvents.map((event, i) => (
              <div key={event.id} className="relative pl-6 border-l border-slate-800 pb-1">
                <div className={`absolute -left-1.5 top-0.5 w-3 h-3 rounded-full border-2 border-slate-950 ${
                  event.type === 'alert' ? 'bg-amber-500 shadow-[0_0_10px_#f59e0b]' : 
                  event.type === 'success' ? 'bg-emerald-500' : 'bg-cyan-500'
                }`} />
                <div className="flex justify-between items-start mb-1">
                  <h4 className="text-xs font-bold text-slate-200 tracking-wide uppercase">{event.title}</h4>
                  <span className="text-[10px] text-slate-500 font-mono">{event.time}</span>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed font-medium">{event.description}</p>
              </div>
            ))}
          </div>
          <button className="mt-6 w-full py-2 bg-slate-900/50 hover:bg-slate-900 border border-slate-800 rounded text-[10px] font-bold text-slate-400 uppercase tracking-widest transition-all">
            Open Full Log
          </button>
        </HoloCard>
      </div>
    </div>
  );
};

export default Overview;